import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ArticleRow {
  readonly articleId: string;
  readonly title: string;
  readonly category: string;
  readonly status: string;
  readonly viewCount: number;
  readonly updatedAt: string | null;
}

const CATEGORIES = ['', 'ONBOARDING', 'POLICY', 'HOW_TO', 'TROUBLESHOOTING', 'FAQ', 'RELEASE_NOTES'];

/**
 * GR L27.01 — Knowledge Base (`/kb`). The reader-facing catalogue of published
 * `kb_articles`: title, category, view count and last update, each linking to
 * the article reader (L27.02). Filterable by category.
 */
export function GrKbPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<ArticleRow[]>([]);
  const [category, setCategory] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams({ publishedOnly: 'true' });
    if (category) params.set('category', category);
    apiRequest<{ data: ArticleRow[] }>(`/v1/collab/knowledge?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, category]);

  return (
    <main aria-label="knowledge-base">
      <h1>{t('gr27.kb.title')}</h1>
      <div className="filters">
        <label>
          {t('gr27.kb.category')}
          <select aria-label="filter-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || t('gr27.kb.all')}</option>)}
          </select>
        </label>
        <Link aria-label="author-link" to="/kb/author">{t('gr27.kb.author')}</Link>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr27.kb.loading')}</p> : (
        <>
          <p aria-label="article-count">{t('gr27.kb.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr27.kb.empty')}</p> : (
            <table aria-label="article-table">
              <thead><tr><th>{t('gr27.kb.colTitle')}</th><th>{t('gr27.kb.category')}</th><th>{t('gr27.kb.colStatus')}</th><th>{t('gr27.kb.colViews')}</th><th>{t('gr27.kb.colUpdated')}</th></tr></thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.articleId}>
                    <td><Link aria-label={`open-${a.articleId}`} to={`/kb/articles/${a.articleId}`}>{a.title}</Link></td>
                    <td>{a.category}</td><td>{a.status}</td><td>{a.viewCount}</td><td>{at(a.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
